/*
 *** Usage in FO: OK
 *** Usage in PO: OK
 *** Usage in BO: OK
 */
import { useFormikContext, useField } from 'formik';
import { cloneDeep, debounce } from 'lodash';
import Quill from 'quill';
import type { StringMap } from 'quill';
import QuillEmitter from 'quill/core/emitter';
import type { MutableRefObject, ForwardedRef } from 'react';
import {
  forwardRef,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDropzone } from 'react-dropzone';
import { animated, useTransition } from 'react-spring';
import styled, { css } from 'styled-components';

import { AdditionalInformation } from '@hivebrite/design-system/AdditionalInformation';
import { Box } from '@hivebrite/design-system/Box';
import type { BoxProps } from '@hivebrite/design-system/Box';
import type { EmojiPickerProps } from '@hivebrite/design-system/EmojiPicker';
import { Loader } from '@hivebrite/design-system/Loader';
import type { Theme } from '@hivebrite/design-system/Theme';
import { toast } from '@hivebrite/design-system/Toast';

import {
  IMAGE,
  ATTACHMENT,
  VIDEO,
} from 'hb-react/shared/components/DesignSystem/QuillEditor/constants';
import { QuillEditorProvider } from 'hb-react/shared/components/DesignSystem/QuillEditor/contexts/QuillEditorContext';
import type { Quill as QuillType } from 'hb-react/shared/components/DesignSystem/QuillEditor/contexts/QuillEditorContext';
import {
  updateInlineImagesFiles,
  removeInlineImageBlot,
  defineFileType,
  filterFilesByType,
  getContentValidator,
  preProcessContentHtml,
  isContentHtmlEmpty,
  wrapCustomVariables,
  unwrapCustomVariables,
} from 'hb-react/shared/components/DesignSystem/QuillEditor/utils';
import shouldDisplayError from 'hb-react/shared/components/formik/utils/shouldDisplayError';
import useInputAndDropdownTheme from 'hb-react/shared/hooks/theme/useInputAndDropdownTheme';
import { FILES_VALIDATION_PROCESSING_FILES } from 'hb-react/shared/hooks/useGlobalStorageV2/constants';
import type { GlobalStorageFile } from 'hb-react/shared/hooks/useGlobalStorageV2/types';
import { getFilesValidator } from 'hb-react/shared/hooks/useGlobalStorageV2/utils/validators';
import useVirusScanningEnabled from 'hb-react/shared/hooks/useVirusScanningEnabled';
import * as errorReporting from 'hb-react/shared/utils/errorReporting';
import ThemeProvider from 'hb-react/style/ThemeProvider';

import Attachments from './Attachments';
import ContentEditable from './ContentEditable';
import EmbedLy from './EmbedLy';
import EmojiPickerFlyout from './EmojiPickerFlyout';
import Images from './Images';
import Toolbar, {
  ACCEPT_ATTACHMENTS,
  ACCEPT_IMAGES,
  ACCEPT_VIDEOS,
} from './Toolbar';
import ToolbarFloat from './ToolbarFloat';
import type { QuillEditorV3Props, QuillEditorV3Ref } from './types';
import { scrollIntoView } from './utils/scrollIntoView';
import useUploadField from './utils/useUploadField';
import Videos from './Videos';

const Mentions = lazy(() => import('./Mentions'));

const Delta = Quill.import('delta');

const QuillEditorV3 = forwardRef(
  (
    {
      acceptAllFiles,
      ariaDescribedBy,
      ariaLabel,
      ariaLabelledBy,
      autoFocus,
      compact,
      containerElement,
      contentEditableRef: contentEditableRefOverride,
      hasError,
      id,
      initialEmbed = null,
      isDisabled,
      isRequired = false,
      maxHeight,
      maxLength,
      maxUploadSize,
      maxAttachmentSize,
      minHeight,
      name = 'content_html',
      uploadsFieldName = 'storage_uploads',
      attachmentsFieldName = 'storage_attachments',
      onAddEmbed,
      onAddLink,
      onBlur,
      onFocus,
      onRemoveEmbed,
      onToggleEmojiPicker,
      options: initialOptions = {},
      placeholder,
      requiredErrorMessage,
      singleImageUpload = false,
      disableValidationOnBlur = false,
    }: QuillEditorV3Props,
    ref: ForwardedRef<QuillEditorV3Ref>,
  ) => {
    const { submitCount } = useFormikContext();

    const inputAndDropdownTheme = useInputAndDropdownTheme();
    const virusScanningEnabled = useVirusScanningEnabled();

    const firstRender = useRef(true);
    const forceEditorFocusRef = useRef(false);
    const contentEditableRefDefault = useRef();
    const editorWrapperRef = useRef<HTMLDivElement>(null);
    const contentEditableRef =
      contentEditableRefOverride || contentEditableRefDefault;
    const onEditorEventsRef = useRef<Function[]>([]);
    const quillRef = useRef<QuillType>();
    const toolbarRef = useRef<HTMLDivElement>(null);
    // Improve types..
    const embedLyListRef: MutableRefObject<any[]> = useRef([]);
    const embedLyRef: MutableRefObject<any> = useRef();
    const imagesRef: MutableRefObject<any> = useRef();
    const validateAndTouchContent: MutableRefObject<boolean> = useRef(true);

    const [isFocus, setIsFocus] = useState(false);
    const [displayLinkModal, setDisplayLinkModal] = useState(false);
    const [displayEmojiPicker, setDisplayEmojiPicker] = useState(false);
    const [displayVideoModal, setDisplayVideoModal] = useState(false);
    const [mentionsBoxId, setMentionsBoxId] = useState<string | null>(null);
    const [activeMentionOptionId, setActiveMentionOptionId] = useState<
      string | null
    >(null);
    const [format, setFormat] = useState<StringMap | null>(null);

    const imagesAreInline = initialOptions.toolbar?.inlineImage;

    const filesValidator = useMemo(
      () => getFilesValidator(virusScanningEnabled),
      [virusScanningEnabled],
    );

    // CONTENT HTML
    const contentValidator = useMemo(
      () =>
        getContentValidator({
          quillRef,
          isRequired,
          maxLength,
          requiredErrorMessage,
        }),
      [isRequired, maxLength, requiredErrorMessage],
    );

    // The field contains the data we will send to the backend on form submission
    const [contentHtmlField, contentHtmlFieldMeta, contentHtmlFieldHelper] =
      useField({
        name,
        validate: contentValidator,
      });

    const contentValueRef = useRef(contentHtmlFieldMeta.initialValue);

    useEffect(() => {
      contentValueRef.current = contentHtmlFieldMeta.value;
    }, [contentHtmlFieldMeta.value]);

    const initialContentValue = useMemo(() => {
      if (initialOptions.toolbar?.customVariables) {
        return wrapCustomVariables(contentHtmlFieldMeta.initialValue);
      }

      return contentHtmlFieldMeta.initialValue;
    }, [
      contentHtmlFieldMeta.initialValue,
      initialOptions.toolbar?.customVariables,
    ]);

    // UPLOADS
    const {
      files: uploads,
      field: uploadsField,
      fieldMeta: uploadsFieldMeta,
      setFiles: setUploads,
      addFiles: addUploadsFiles,
      removeFiles: removeUploadsFiles,
    } = useUploadField({
      fieldName: uploadsFieldName,
      filesValidator,
      initialContentHtml: imagesAreInline
        ? contentHtmlFieldMeta.initialValue
        : null,
      maxFileSize: maxUploadSize,
    });

    const uploadsFilesRef = useRef(uploads);

    // ATTACHMENTS
    const {
      files: attachments,
      field: attachmentsField,
      fieldMeta: attachmentsFieldMeta,
      setFiles: setAttachments,
      addFiles: addAttachmentsFiles,
      removeFiles: removeAttachmentsFiles,
    } = useUploadField({
      fieldName: attachmentsFieldName,
      filesValidator,
      maxFileSize: maxAttachmentSize,
    });

    useEffect(() => {
      if (autoFocus) {
        quillRef.current?.focus();
      }
    }, [autoFocus]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const updateContentHtml = useCallback(
      debounce(() => {
        const uploadsError = filesValidator(uploadsFilesRef.current);

        if (uploadsError) {
          return;
        }

        if (!quillRef.current) {
          return;
        }

        try {
          let newValue = preProcessContentHtml({
            quillRef,
            files: uploadsFilesRef.current,
            uploadsFieldName,
          });

          if (initialOptions.toolbar?.customVariables) {
            newValue = unwrapCustomVariables(newValue);
          }

          const bothEmpty =
            isContentHtmlEmpty(newValue) &&
            isContentHtmlEmpty(contentValueRef.current);
          const validateAndTouch = validateAndTouchContent.current;

          if (newValue !== contentValueRef.current && !bothEmpty) {
            contentHtmlFieldHelper.setValue(newValue, validateAndTouch);
            contentHtmlFieldHelper.setTouched(validateAndTouch, false);
          }

          validateAndTouchContent.current = true;
        } catch (error) {
          errorReporting.captureException(error, {
            tags: { tribe: 'evolution-tribe' },
          });
        }
      }, 300),
      [contentHtmlFieldHelper, uploadsFieldName],
    );

    // Update content when uploads change
    useEffect(() => {
      if (firstRender.current) {
        firstRender.current = false;

        return;
      }

      uploadsFilesRef.current = uploads;
      updateContentHtml();
    }, [updateContentHtml, uploads]);

    // Update content when text changes
    const handleTextChange = useCallback(() => {
      if (quillRef.current) {
        if (imagesAreInline) {
          const contents = quillRef.current.getContents();
          const { ops } = contents;

          setUploads((prevFiles) => updateInlineImagesFiles(prevFiles, ops));
        }

        updateContentHtml();
      }
    }, [imagesAreInline, setUploads, updateContentHtml]);

    const setDisplayVideoModalCallback = useCallback(
      (value) => {
        if (value) {
          // We retrieve these here instead of inside the AddLinkModal to avoid the following error
          // because calling getFormat() below triggers handleFocus
          // of 'app/assets/javascripts/react/shared/components/DesignSystem/QuillEditor/index.jsx'
          // to be called which in turns calls setIsFocus(true) and causes the error
          // ERROR: react_devtools_backend.js:3973 Warning: Cannot update a component (`ForwardRef`) while
          // rendering a different component (`AddLinkModal`).
          if (quillRef.current) {
            const selection = quillRef.current.getSelection();
            const format = selection ? quillRef.current.getFormat() : {};

            setFormat(format);
          }
        }

        setDisplayVideoModal(value);
      },
      [quillRef],
    );

    const setDisplayLinkModalCallback = useCallback(
      (value) => {
        if (value) {
          // We retrieve these here instead of inside the AddLinkModal to avoid the following error
          // because calling getFormat() below triggers handleFocus
          // of 'app/assets/javascripts/react/shared/components/DesignSystem/QuillEditor/index.jsx'
          // to be called which in turns calls setIsFocus(true) and causes the error
          // ERROR: react_devtools_backend.js:3973 Warning: Cannot update a component (`ForwardRef`) while
          // rendering a different component (`AddLinkModal`).
          if (quillRef.current) {
            const selection = quillRef.current?.getSelection();
            const format = selection ? quillRef.current?.getFormat() : {};

            setFormat(format);
          }
        }

        setDisplayLinkModal(value);
      },
      [quillRef],
    );

    const fileAttachments = useMemo(
      () => attachments.filter(({ isRemoved }) => !isRemoved),
      [attachments],
    );

    const imageAttachments = useMemo(
      () =>
        uploads
          .filter(({ isRemoved }) => !isRemoved)
          .filter(({ file, fileMetadata }: GlobalStorageFile) =>
            ACCEPT_IMAGES.includes(file?.type || fileMetadata?.type || ''),
          ),
      [uploads],
    );

    const videoAttachments = useMemo(
      () =>
        uploads
          .filter(({ isRemoved }) => !isRemoved)
          .filter(({ file, fileMetadata }: GlobalStorageFile) =>
            ACCEPT_VIDEOS.includes(file?.type || fileMetadata?.type || ''),
          ),
      [uploads],
    );

    const hasFileAttachments = fileAttachments.length > 0;
    const hasImageAttachments = imageAttachments.length > 0;
    const hasVideoAttachments = videoAttachments.length > 0;

    const {
      images: imagesToolbar,
      attachments: attachmentsToolbar,
      video: videoToolbar,
    } = initialOptions?.toolbar ?? {};

    // This is for the image toolbar. The inlineImage toolbar is unrelated to that
    const canUploadImages =
      imagesToolbar &&
      !hasFileAttachments &&
      !hasVideoAttachments &&
      (!singleImageUpload || !hasImageAttachments);

    // When toolbar.inlineImage is enabled, it's possible to have image attachments (inline in the content)
    // and still allow users to attach files.
    const canUploadFiles =
      attachmentsToolbar &&
      (!hasImageAttachments || !imagesToolbar) &&
      (!hasVideoAttachments || !videoToolbar);

    const canUploadVideo =
      videoToolbar &&
      !hasFileAttachments &&
      // Only one video is allowed.
      !hasVideoAttachments &&
      !hasImageAttachments;

    const options = useMemo(() => {
      // Deep copy initialOptions
      const newOptions = cloneDeep(initialOptions);

      if (!newOptions.toolbar) {
        return newOptions;
      }

      if (imagesToolbar) {
        newOptions.toolbar.images = {
          // @ts-expect-error we need this for cases where images is an object
          ...newOptions.toolbar.images,
          disabled: !canUploadImages,
        };
      }

      if (attachmentsToolbar) {
        newOptions.toolbar.attachments = {
          // @ts-expect-error we need this for cases where attachments is an object
          ...newOptions.toolbar.attachments,
          disabled: !canUploadFiles,
        };
      }

      if (videoToolbar) {
        newOptions.toolbar.video = {
          // @ts-expect-error we need this for cases where video is an object
          ...newOptions.toolbar.video,
          disabled: !canUploadVideo,
        };
      }

      return newOptions;
    }, [
      initialOptions,
      imagesToolbar,
      attachmentsToolbar,
      videoToolbar,
      canUploadImages,
      canUploadFiles,
      canUploadVideo,
    ]);

    const hasToolbarFloat = useMemo(
      () =>
        options.toolbarFloats
          ? options.toolbarFloats?.[0].items?.length !== 0
          : false,
      [options],
    );

    const hasEmbedMounted = useMemo(
      () => options.embedLy && !hasFileAttachments && !hasImageAttachments,
      [options, attachments, uploads],
    );

    // Pass this reference to child component for add/remove "onChange" event from QuillJS
    const handleAddEditorChangeEvent = useCallback((fn: Function) => {
      onEditorEventsRef.current.push(fn);
    }, []);

    const handleRemoveEditorChangeEvent = useCallback((fn: Function) => {
      onEditorEventsRef.current = onEditorEventsRef.current.filter(
        (event) => event !== fn,
      );
    }, []);

    const handleEditorChange = useCallback(
      (eventName) => {
        onEditorEventsRef.current.forEach((event: Function) =>
          event(eventName),
        );

        if (eventName === QuillEmitter.events.TEXT_CHANGE) {
          handleTextChange();
        }

        if (!quillRef.current) return;

        const selection = quillRef.current.getSelection();

        // Don't do anything if we don't have selection from QuillJS
        if (selection === null) {
          return;
        }

        scrollIntoView(quillRef.current, selection, toolbarRef.current);

        const selectionFormat = quillRef.current.getFormat();

        setFormat(selectionFormat);
      },
      [handleTextChange],
    );

    const handleForceEditorFocus = useCallback((focus) => {
      forceEditorFocusRef.current = focus;
    }, []);

    useEffect(() => {
      quillRef.current?.off(
        QuillEmitter.events.EDITOR_CHANGE,
        handleEditorChange,
      );

      contentEditableRef.current
        ?.getQuillRef()
        ?.on(QuillEmitter.events.EDITOR_CHANGE, handleEditorChange);
    }, [contentEditableRef, handleEditorChange]);

    const handleFocus = useCallback(
      (e) => {
        setIsFocus(true);

        if (onFocus) {
          onFocus(e);
        }
      },
      [onFocus],
    );

    const handleBlur = useCallback(
      (e) => {
        if (!forceEditorFocusRef.current) {
          setIsFocus(false);
        }

        if (onBlur) {
          onBlur(e);
        }

        if (!disableValidationOnBlur) {
          contentHtmlFieldHelper.setTouched(true, true);
        }
      },
      [disableValidationOnBlur, contentHtmlFieldHelper, onBlur],
    );

    const handleAddEmbed = useCallback(
      (response) => {
        const lastEmbed =
          embedLyListRef.current[embedLyListRef.current.length - 1];

        // Don't add embed if the last embed are the same
        if (lastEmbed?.url !== response?.url) {
          embedLyListRef.current.push(response);

          if (onAddEmbed) {
            onAddEmbed(response);
          }
        }
      },
      [onAddEmbed],
    );

    const handleCloseEmbed = useCallback(() => {
      // Remove last entry
      embedLyListRef.current.pop();

      if (embedLyListRef.current.length > 0) {
        // Apply the last entry to embedLy
        const lastEntry =
          embedLyListRef.current[embedLyListRef.current.length - 1];

        embedLyRef.current.setEmbed(lastEntry);

        if (onAddEmbed) {
          onAddEmbed(lastEntry);
        }
      } else if (onRemoveEmbed) {
        onRemoveEmbed();
      }
    }, [onRemoveEmbed, onAddEmbed]);

    const handleAddFiles = useCallback(
      async ({
        type,
        files: addedFiles,
        keys,
      }: {
        type: 'attachment' | 'image' | 'video';
        files: File[];
        keys?: string[];
      }): Promise<void> => {
        const addFiles =
          type === IMAGE || type === VIDEO
            ? addUploadsFiles
            : addAttachmentsFiles;

        const filesData = addedFiles.map((file, index) => ({
          file,
          key: keys?.[index],
        }));

        await addFiles(filesData);
      },
      [addUploadsFiles, addAttachmentsFiles],
    );

    const handleRemoveFile = useCallback(
      (removedFile) => {
        removeUploadsFiles({ files: [removedFile] });
        removeAttachmentsFiles({ files: [removedFile] });

        if (!quillRef.current) {
          throw new Error('Quill ref is not defined');
        }

        if (imagesAreInline) {
          const contents = quillRef.current.getContents();
          const newOps = removeInlineImageBlot({
            ops: contents.ops,
            keys: [removedFile.key],
          });

          quillRef.current?.setContents(new Delta(newOps));
        }

        // Force focus after remove file
        quillRef.current?.focus();
      },
      [imagesAreInline, removeUploadsFiles, removeAttachmentsFiles],
    );

    const handleDrop = useCallback(
      (acceptedFiles) => {
        const type = defineFileType(acceptedFiles);

        if (!type) {
          throw new Error('Cannot define file type');
        }

        const noMixedTypesError = __(
          'Cannot attach images and other files to the same post',
        );

        try {
          if (type === IMAGE && initialOptions.toolbar?.inlineImage) {
            throw new Error(
              __('Please use the insert image button to insert inline images.'),
            );
          }

          if (type === IMAGE && !canUploadImages) {
            throw new Error(noMixedTypesError);
          }

          if (type === ATTACHMENT && !canUploadFiles) {
            throw new Error(noMixedTypesError);
          }

          if (type === VIDEO && !canUploadVideo) {
            throw new Error(noMixedTypesError);
          }

          let types = ACCEPT_ATTACHMENTS;

          types = type === IMAGE ? ACCEPT_IMAGES : types;
          types = type === VIDEO ? ACCEPT_VIDEOS : types;

          const files = filterFilesByType({
            files: acceptedFiles,
            types,
          });

          if (files.length !== acceptedFiles.length) {
            throw new Error(noMixedTypesError);
          }

          if (files.length > 0) {
            handleAddFiles({
              files,
              type,
            });
          }
        } catch (error) {
          errorReporting.captureException(error, {
            tags: { tribe: 'evolution-tribe' },
          });
          if (error.message) {
            toast.error(error.message);
          }
        }
      },
      [
        canUploadFiles,
        canUploadImages,
        canUploadVideo,
        handleAddFiles,
        initialOptions.toolbar?.inlineImage,
      ],
    );

    const { getRootProps, isDragActive } = useDropzone({ onDrop: handleDrop });

    // Only enabled Dropzone if we have images/files in Toolbar
    const hasDropzone = !isDisabled && (canUploadImages || canUploadFiles);

    const transitionsDrop = useTransition(hasDropzone && isDragActive, {
      from: {
        opacity: 0,
        pointerEvents: 'none',
      },
      enter: {
        opacity: 1,
        pointerEvents: 'all',
      },
      leave: {
        opacity: 0,
        pointerEvents: 'none',
      },
    });

    const handleEmojiClick = useCallback<EmojiPickerProps['onEmojiPick']>(
      (emoji) => {
        if (!quillRef.current) {
          throw new Error('Quill ref is not defined');
        }

        const quill = quillRef.current;

        quill.focus();

        const selection = quill.getSelection();

        if (selection) {
          const { index: cursorPos } = selection;

          const insert = emoji.emoji;

          const delta = new Delta().retain(cursorPos).insert(insert);

          quillRef.current.updateContents(delta);

          // Update the cursor position after adding the emoji
          setTimeout(() => {
            quill.setSelection(
              cursorPos + insert.length,
              0,
              Quill.sources.SILENT,
            );
          }, 1);
        }

        setDisplayEmojiPicker(false);
      },
      [quillRef],
    );

    const handleClickEmojiPicker = useCallback(() => {
      setDisplayEmojiPicker(!displayEmojiPicker);

      if (onToggleEmojiPicker) {
        onToggleEmojiPicker(!displayEmojiPicker);
      }
    }, [displayEmojiPicker, onToggleEmojiPicker]);

    // DidMount
    useEffect(() => {
      if (ref) {
        if (typeof ref === 'function') {
          throw new Error('QuillEditor forwarded ref is a function');
        }

        const clearContents = () => {
          // Disable validation and touch of the content for the next update
          validateAndTouchContent.current = false;

          // @ts-expect-error this works, maybe a use-case they forgot in quill?
          quillRef.current?.setContents(null);
        };

        // Share some functions in ref
        ref.current = {
          focus: () => quillRef.current?.focus(),
          clearContents,
          clearAllContents: () => {
            clearContents();
            setUploads([]);
            setAttachments([]);

            // Only do this if we have embedLy enable
            if (embedLyRef.current) {
              embedLyListRef.current = [];
              embedLyRef.current.clearEmbed();
            }
          },
          addEmbed: (url) => {
            if (embedLyRef.current) {
              embedLyRef.current.setPlainText(url);
            }
          },
          getContentEditableRef: () => quillRef.current,
          getForceEditorFocusRef: () => forceEditorFocusRef.current,
        };
      }

      quillRef.current = contentEditableRef.current?.getQuillRef();
    }, [
      contentEditableRef,
      contentHtmlFieldHelper,
      name,
      ref,
      setAttachments,
      setUploads,
    ]);

    useEffect(() => {
      // If there's an embedded link, we need to remove it if user uploads a file or image
      if (onRemoveEmbed && (hasFileAttachments || hasImageAttachments)) {
        onRemoveEmbed();
      }
    }, [hasFileAttachments, hasImageAttachments, onRemoveEmbed]);

    // We only display one error message at a time for files.
    const filesError = uploadsFieldMeta.error || attachmentsFieldMeta.error;

    return (
      <ThemeProvider
        theme={inputAndDropdownTheme.themeName}
        fontBrightness={inputAndDropdownTheme.fontBrightness}
      >
        <QuillEditorProvider
          acceptAllFiles={acceptAllFiles}
          attachments={fileAttachments}
          compact={compact}
          displayLinkModal={displayLinkModal}
          displayVideoModal={displayVideoModal}
          editorWrapperRef={editorWrapperRef}
          files={[...uploads, ...attachments]}
          format={format}
          id={id}
          images={imageAttachments}
          initialEmbed={initialEmbed}
          isEditorFocus={isFocus}
          offEditorChange={handleRemoveEditorChangeEvent}
          onAddEmbed={handleAddEmbed}
          onAddFiles={handleAddFiles}
          onAddLink={onAddLink}
          onEditorChange={handleAddEditorChangeEvent}
          onEditorEventsRef={onEditorEventsRef}
          onEmojiClick={handleEmojiClick}
          onRemoveEmbed={handleCloseEmbed}
          onRemoveFile={handleRemoveFile}
          options={options}
          quillRef={quillRef}
          setActiveMentionOptionId={setActiveMentionOptionId}
          setDisplayLinkModal={setDisplayLinkModalCallback}
          setDisplayVideoModal={setDisplayVideoModalCallback}
          setForceEditorFocus={handleForceEditorFocus}
          setMentionsBoxId={setMentionsBoxId}
          toggleEmojiPicker={handleClickEmojiPicker}
          toolbarRef={toolbarRef}
          uploads={imageAttachments}
          videos={videoAttachments}
        >
          <Wrapper
            {...(hasDropzone && getRootProps())}
            minHeight={minHeight}
            tabIndex={-1}
          >
            {transitionsDrop(
              (styles, item) =>
                item && (
                  <animated.div
                    // @ts-expect-error style prop
                    style={styles}
                    css={css`
                      position: absolute;
                      top: 0;
                      bottom: 0;
                      left: 0;
                      right: 0;
                      z-index: 10;
                    `}
                  >
                    <WrapperDrop>
                      {__('Drop your files to upload.')}
                    </WrapperDrop>
                  </animated.div>
                ),
            )}
            <EditorWrapper
              ref={editorWrapperRef}
              $hasError={Boolean(hasError)}
              $isFocus={isFocus}
            >
              <ContentEditable
                ref={contentEditableRef}
                // @ts-expect-error forwardRef type errors
                ariaDescribedBy={ariaDescribedBy}
                ariaLabel={ariaLabel}
                ariaLabelledBy={ariaLabelledBy}
                autoFocus={autoFocus}
                id={id}
                initialValue={initialContentValue}
                isDisabled={isDisabled}
                isRequired={isRequired}
                maxHeight={maxHeight}
                onBlur={handleBlur}
                onFocus={handleFocus}
                padding={3}
                placeholder={placeholder}
                mentionsBoxId={mentionsBoxId}
                activeMentionOptionId={activeMentionOptionId}
                toolbarRef={toolbarRef}
              />

              {hasEmbedMounted && <EmbedLy ref={embedLyRef} />}
              {hasImageAttachments && (
                <Images
                  ref={imagesRef}
                  // @ts-expect-error forwardRef type errors
                  singleImageUpload={singleImageUpload}
                  errorsOnly={imagesAreInline}
                />
              )}
              {hasVideoAttachments && <Videos />}
              <Attachments />

              {initialOptions.toolbar && <Toolbar ref={toolbarRef} />}
            </EditorWrapper>
            {hasToolbarFloat && (
              <WrapperToolbarFloat>
                <ToolbarFloat containerElement={containerElement} />
              </WrapperToolbarFloat>
            )}
            <Suspense fallback={null}>
              {options.toolbar?.mentions && <Mentions />}
            </Suspense>
            {displayEmojiPicker && <EmojiPickerFlyout />}
          </Wrapper>
          {shouldDisplayError({
            meta: contentHtmlFieldMeta,
            form: { submitCount },
          }) &&
            contentHtmlFieldMeta.error && (
              <AdditionalInformation
                type="error"
                mt={4}
                id={contentHtmlField.name}
              >
                {contentHtmlFieldMeta.error}
              </AdditionalInformation>
            )}
          {shouldDisplayError({
            meta: {
              error: uploadsFieldMeta.error || attachmentsFieldMeta.error,
              touched: uploadsFieldMeta.touched || attachmentsFieldMeta.touched,
            },
            form: { submitCount },
          }) &&
            filesError &&
            filesError !== FILES_VALIDATION_PROCESSING_FILES && (
              <AdditionalInformation
                type="error"
                mt={4}
                id={
                  uploadsFieldMeta.error
                    ? uploadsField.name
                    : attachmentsField.name
                }
              >
                {filesError}
              </AdditionalInformation>
            )}
          {filesError === FILES_VALIDATION_PROCESSING_FILES && (
            <Box mt={2}>
              <Box flex="0 0 auto">
                <Loader size={20} />
              </Box>
              <Box flex="1 1 100%" ml={2}>
                {__('Files are being processed. Please wait.')}
              </Box>
            </Box>
          )}
        </QuillEditorProvider>
      </ThemeProvider>
    );
  },
);

export default QuillEditorV3;

const Wrapper = styled(Box).attrs<any>(() => ({
  flex: 1,
}))`
  position: relative;
  width: 100%;
  word-break: break-word;
`;
const WrapperDrop = styled(Box).attrs(() => ({
  alignItems: 'center',
  bg: 'white',
  color: 'primary',
  height: '100%',
  justifyContent: 'center',
  width: '100%',
}))`
  border: 1px solid ${(props) => props.theme.colors.primary};
  border-radius: 4px;
  position: relative;

  &:after {
    background-color: ${(props) => props.theme.colors.primary};
    bottom: 0;
    content: ' ';
    left: 0;
    opacity: 0.05;
    position: absolute;
    right: 0;
    top: 0;
  }
`;

interface EditorWrapperProps extends BoxProps {
  $hasError: boolean;
  $isFocus: boolean;
  theme: Theme;
}

const EditorWrapper = styled(Box).attrs(({ theme }) => ({
  bg: theme.input.bg,
  color: theme.input.color,
  flexDirection: 'column',
}))<EditorWrapperProps>`
  border: 1px solid;
  border-color: ${({ $hasError, $isFocus, theme }: EditorWrapperProps) =>
    $isFocus
      ? theme.colors?.primary
      : $hasError
        ? theme.colors?.fire
        : theme.input?.borderColor};
  border-radius: ${({ theme }: EditorWrapperProps) =>
    `${theme.input?.borderRadius}px`};
  user-select: none;
  width: 100%;
`;
const WrapperToolbarFloat = styled(Box)`
  bottom: 0;
  left: 0;
  padding: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
`;
